/*!
 * Inayah — Size Guide Modal Rebuild (clean implementation)
 *
 * Goals (per spec docs/specs/2026-05-02-size-guide-modal-mobile.md):
 *  - Full-viewport overlay with dimmed backdrop on mobile + desktop
 *  - Modal opens scrolled to top (INCH/CM toggle visible)
 *  - Page is scroll-locked while modal is open (no scroll bleed)
 *  - Modal dismissed via X, backdrop tap, or Escape key
 *  - Page returns to original scroll position on close (no jumps)
 *
 * Strategy:
 *  1. On load, teleport every size-chart modal to be a direct child of <body>.
 *     This escapes any ancestor with transform/filter/will-change that would
 *     otherwise trap position:fixed inside its containing block.
 *  2. Inject our override CSS via a <style> appended to <body> end. This
 *     guarantees it loads AFTER the customizer's inline <style> blocks and
 *     wins on cascade order with equal specificity.
 *  3. Use an ID-prefix attribute selector PLUS a class we add (.inayah-modal)
 *     to lift specificity above the customizer's #id rules.
 *  4. Use overflow:hidden + touch-action:none for scroll lock. NEVER use
 *     position:fixed on body — that would re-trap descendant fixed elements.
 *  5. Watch the modal's class list for opacity-100 / pointer-events-none
 *     transitions to detect open/close and toggle scroll lock + scroll-reset.
 */
(function () {
  "use strict";

  var STYLE_ID = "inayah-size-guide-modal-css";
  var INAYAH_CLASS = "inayah-modal";
  var locked = false;

  // ─── CSS ───────────────────────────────────────────────────────────────
  // Uses [id^=...] + .inayah-modal for higher specificity than the
  // customizer's #full-id selector (which is 0,1,0,0). Our combined
  // selector is 0,1,1,1 — wins.
  var CSS = [
    /* Bottom sheet on mobile: covers ~80% of viewport, product photo stays visible above */
    '[id^="custom-size-chart-modal-"].inayah-modal {',
    '  position: fixed !important;',
    '  top: 0 !important;',
    '  left: 0 !important;',
    '  right: 0 !important;',
    '  bottom: 0 !important;',
    '  width: 100vw !important;',
    '  height: 100vh !important;',
    '  height: 100dvh !important;',
    '  margin: 0 !important;',
    '  padding: 0 !important;',
    '  z-index: 9999 !important;',
    '  display: flex !important;',
    '  align-items: flex-end !important;',
    '  justify-content: center !important;',
    '  background-color: rgba(0, 0, 0, 0.45) !important;',
    '  overflow: hidden !important;',
    '  -webkit-overflow-scrolling: auto !important;',
    '}',

    '[id^="custom-size-chart-modal-"].inayah-modal.pointer-events-none {',
    '  display: none !important;',
    '}',

    '[id^="custom-size-chart-modal-"].inayah-modal .sf-modal__content {',
    '  margin: 0 !important;',
    '  width: 100% !important;',
    '  max-width: 100% !important;',
    '  height: auto !important;',
    '  max-height: 80vh !important;',
    '  max-height: 80dvh !important;',
    '  border-top-left-radius: 16px !important;',
    '  border-top-right-radius: 16px !important;',
    '  border-bottom-left-radius: 0 !important;',
    '  border-bottom-right-radius: 0 !important;',
    '  background: #fff !important;',
    '  overflow-y: auto !important;',
    '  overscroll-behavior: contain !important;',
    '  -webkit-overflow-scrolling: touch !important;',
    '  padding-bottom: env(safe-area-inset-bottom, 0px) !important;',
    '  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.15) !important;',
    '}',

    /* Drag-handle indicator at top of sheet */
    '[id^="custom-size-chart-modal-"].inayah-modal .sf-modal__content::before {',
    '  content: "" !important;',
    '  display: block !important;',
    '  width: 40px !important;',
    '  height: 4px !important;',
    '  background: #d4d4d4 !important;',
    '  border-radius: 2px !important;',
    '  margin: 8px auto 4px !important;',
    '  position: sticky !important;',
    '  top: 8px !important;',
    '}',

    '[id^="custom-size-chart-modal-"].inayah-modal .sf-modal__close {',
    '  position: absolute !important;',
    '  top: 12px !important;',
    '  right: 12px !important;',
    '  width: 36px !important;',
    '  height: 36px !important;',
    '  display: flex !important;',
    '  align-items: center !important;',
    '  justify-content: center !important;',
    '  background: transparent !important;',
    '  z-index: 10 !important;',
    '}',

    '@media (min-width: 768px) {',
    '  [id^="custom-size-chart-modal-"].inayah-modal {',
    '    align-items: flex-start !important;',
    '    padding: 5vh 20px !important;',
    '  }',
    '  [id^="custom-size-chart-modal-"].inayah-modal .sf-modal__content {',
    '    max-width: 720px !important;',
    '    height: auto !important;',
    '    max-height: 90vh !important;',
    '    border-radius: 8px !important;',
    '  }',
    '  [id^="custom-size-chart-modal-"].inayah-modal .sf-modal__content::before {',
    '    display: none !important;',
    '  }',
    '}',

    /* Brand-align the "Size guide" header */
    '[id^="custom-size-chart-modal-"].inayah-modal h2.text-2xl {',
    '  text-transform: uppercase !important;',
    '  letter-spacing: 2px !important;',
    '  font-size: 13px !important;',
    '  font-weight: 500 !important;',
    '  color: #1a1a1a !important;',
    '  text-align: center !important;',
    '  margin: 0 0 16px !important;',
    '  padding-top: 4px !important;',
    '}',

    'html.inayah-modal-locked,',
    'html.inayah-modal-locked body {',
    '  overflow: hidden !important;',
    '  touch-action: none !important;',
    '  -webkit-overflow-scrolling: auto !important;',
    '}'
  ].join('\n');

  function injectCSS() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    // Append to END of body so it loads AFTER customizer inline styles
    (document.body || document.documentElement).appendChild(style);
  }

  // ─── DOM helpers ───────────────────────────────────────────────────────
  function getModals() {
    return document.querySelectorAll('[id^="custom-size-chart-modal-"]');
  }

  function isOpen(modal) {
    return !modal.classList.contains('pointer-events-none');
  }

  function teleport(modal) {
    if (modal.__inayahTeleported) return;
    modal.classList.add(INAYAH_CLASS);
    // CAPTURE original product form BEFORE moving — needed so ATC button
    // (which would normally call container.closest('form')) can still find it
    // after we move the modal to <body>.
    var originalForm = modal.closest('form');
    if (originalForm) {
      modal.__inayahFormRef = originalForm;
    }
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
    modal.__inayahTeleported = true;
  }

  // ─── Bridge ATC button: re-implement add-to-cart using the saved form ref ──
  function normSize(val) {
    if (!val) return '';
    return String(val).toLowerCase().replace(/[\s-]/g, '');
  }
  function sizeMatches(variant, size) {
    if (!variant) return false;
    var s = normSize(size);
    for (var i = 1; i <= 5; i++) {
      var opt = variant['option' + i];
      if (opt && normSize(opt) === s) return true;
    }
    return false;
  }
  function bridgeAddToCart(modal) {
    if (modal.__inayahAtcBridged) return;
    modal.__inayahAtcBridged = true;
    var addBtn = modal.querySelector('.add-cart-btn');
    if (!addBtn) return;
    // Use capture phase so we run BEFORE the broken handler that called closest('form')
    addBtn.addEventListener('click', function (e) {
      if (addBtn.classList.contains('disabled') || addBtn.disabled) return;
      var selectedBtn = modal.querySelector('.size-btn.selected');
      if (!selectedBtn) return;
      var selectedSize = selectedBtn.getAttribute('data-size');
      if (!selectedSize) return;
      var form = modal.__inayahFormRef;
      if (!form) return;
      var productId = form.dataset.productId || form.getAttribute('data-product-id');
      var variantInput = form.querySelector('input[name="id"]');
      if (!variantInput || !productId) return;
      var productData = window._themeProducts && window._themeProducts[productId];
      if (!productData || !productData.variants) return;
      var selectedColor = form.querySelector('.product-option-item[data-selected="true"][data-option-type="color"]');
      var colorValue = selectedColor ? selectedColor.dataset.value : null;
      var variant = productData.variants.find(function (v) {
        if (!sizeMatches(v, selectedSize)) return false;
        if (colorValue) {
          var cv = normSize(colorValue);
          for (var i = 1; i <= 5; i++) {
            var opt = v['option' + i];
            if (opt && normSize(opt) === cv) return true;
          }
          return false;
        }
        return true;
      });
      if (!variant) variant = productData.variants.find(function (v) { return sizeMatches(v, selectedSize); });
      if (!variant || !variant.available) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      variantInput.value = variant.id;
      addBtn.disabled = true;
      var origText = addBtn.textContent;
      addBtn.textContent = 'ADDING...';
      var formData = new FormData(form);
      fetch('/cart/add.js', { method: 'POST', body: formData })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data.status === 422) {
            addBtn.disabled = false;
            addBtn.textContent = origText;
            alert(data.description || 'Unable to add item to cart');
            return;
          }
          // Close the modal
          var closeBtn = modal.querySelector('.sf-modal__close, [data-close-modal], [data-modal-close]');
          if (closeBtn) closeBtn.click();
          else modal.classList.add('pointer-events-none');
          // Notify theme to refresh cart drawer
          if (window.Shopify && typeof window.Shopify.onItemAdded === 'function') {
            window.Shopify.onItemAdded(data);
          }
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: data }));
          document.dispatchEvent(new CustomEvent('product:added-to-cart', { detail: { item: data } }));
          // Trigger Minimog cart drawer open if present
          var drawerTrigger = document.querySelector('[data-action="open-drawer"][data-drawer-id="cart-drawer"], .js-drawer-open-cart, [aria-controls="cart-drawer"]');
          if (drawerTrigger) drawerTrigger.click();
          addBtn.disabled = false;
          addBtn.textContent = origText;
        })
        .catch(function () {
          addBtn.disabled = false;
          addBtn.textContent = origText;
          alert('Unable to add item to cart. Please try again.');
        });
    }, true);
  }

  function resetModalScroll(modal) {
    var content = modal.querySelector('.sf-modal__content');
    if (content) {
      try { content.scrollTop = 0; } catch (e) {}
    }
    var tbl = modal.querySelector('.table-responsive');
    if (tbl) {
      try { tbl.scrollLeft = 0; } catch (e) {}
    }
  }

  // ─── Scroll lock (overflow + touch-action; NOT position:fixed) ────────
  function lockBody() {
    if (locked) return;
    document.documentElement.classList.add('inayah-modal-locked');
    locked = true;
  }
  function unlockBody() {
    if (!locked) return;
    document.documentElement.classList.remove('inayah-modal-locked');
    locked = false;
  }

  function syncLock() {
    var anyOpen = false;
    getModals().forEach(function (m) {
      if (isOpen(m)) anyOpen = true;
    });
    if (anyOpen) lockBody();
    else unlockBody();
  }

  // ─── Wire up observers per modal ───────────────────────────────────────
  function wireModal(modal) {
    teleport(modal);
    bridgeAddToCart(modal);
    if (modal.__inayahWired) return;
    modal.__inayahWired = true;
    new MutationObserver(function () {
      syncLock();
      if (isOpen(modal)) {
        // Defer twice to outlast theme transitions
        setTimeout(function () { resetModalScroll(modal); }, 30);
        setTimeout(function () { resetModalScroll(modal); }, 200);
      }
    }).observe(modal, { attributes: true, attributeFilter: ['class'] });
  }

  function wireAll() {
    getModals().forEach(wireModal);
  }

  // ─── Init ──────────────────────────────────────────────────────────────
  function init() {
    injectCSS();
    wireAll();

    // Re-wire if modals are added later
    new MutationObserver(wireAll).observe(document.body, { childList: true, subtree: true });

    // Defensive: bind directly to trigger button so we close any prior anchor
    // jump and ensure scroll lock fires immediately
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var trigger = e.target.closest('[data-open-custom-sizeguide], [data-open-sizeguide]');
      if (trigger) {
        var anchor = trigger.closest('a[href="#"], a[href=""]');
        if (anchor) e.preventDefault();
        setTimeout(syncLock, 30);
        return;
      }
      // Click on close button or backdrop (the modal element itself, not its content)
      var closeBtn = e.target.closest('.sf-modal__close, [data-close-modal]');
      if (closeBtn) {
        setTimeout(syncLock, 30);
        return;
      }
      var modal = e.target.closest('[id^="custom-size-chart-modal-"]');
      if (modal && e.target === modal) {
        // Click on backdrop — mimic theme close behavior
        modal.classList.add('pointer-events-none', 'opacity-0');
        modal.classList.remove('opacity-100');
        setTimeout(syncLock, 30);
      }
    }, true);

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      getModals().forEach(function (m) {
        if (isOpen(m)) {
          m.classList.add('pointer-events-none', 'opacity-0');
          m.classList.remove('opacity-100');
        }
      });
      setTimeout(syncLock, 30);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
